/**
 * Tailwind CSS Utility Functions
 * 
 * This file provides utility functions for working with Tailwind CSS classes.
 * It helps maintain consistency and reduces duplication in component styling.
 */

import { ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Combines multiple class values and merges Tailwind classes intelligently
 * 
 * This function uses clsx to combine class values and tailwind-merge to
 * properly handle Tailwind class conflicts (e.g., merging "px-4 px-6" to "px-6").
 * 
 * @param inputs - Class values to merge (strings, objects, arrays, etc.)
 * @returns A string of merged class names
 * 
 * @example
 * // Basic usage
 * cn('px-4 py-2', 'bg-blue-500')
 * // => 'px-4 py-2 bg-blue-500'
 * 
 * // With conditional classes
 * cn('px-4 py-2', { 'bg-blue-500': isPrimary, 'bg-gray-500': !isPrimary })
 * 
 * // Handling conflicts (last one wins)
 * cn('px-4', 'px-6')
 * // => 'px-6'
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Common class combinations for frequently used patterns
 */
export const twStyles = {
  /**
   * Button variants
   */
  button: {
    base: 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90',
    accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-border bg-transparent hover:bg-muted',
    ghost: 'hover:bg-muted',
    link: 'text-primary underline-offset-4 hover:underline',
    
    // Size variants
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 py-2',
    lg: 'h-12 px-6 text-lg',
  },
  
  /**
   * Card variants
   */
  card: {
    base: 'bg-card text-card-foreground rounded-lg border border-border overflow-hidden',
    
    // Size variants
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  },
  
  /**
   * Input variants
   */
  input: {
    base: 'flex h-10 w-full rounded-md border border-border bg-muted px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  },
  
  /**
   * Container variants
   */
  container: {
    base: 'mx-auto px-4 sm:px-6 lg:px-8',
    sm: 'max-w-3xl',
    md: 'max-w-5xl',
    lg: 'max-w-7xl',
  },
  
  /**
   * Typography variants
   */
  typography: {
    h1: 'text-4xl font-bold tracking-tight sm:text-5xl',
    h2: 'text-3xl font-bold tracking-tight sm:text-4xl',
    h3: 'text-2xl font-bold',
    h4: 'text-xl font-semibold',
    p: 'text-base text-muted-foreground',
    lead: 'text-xl text-muted-foreground',
    large: 'text-lg font-semibold',
    small: 'text-sm text-muted-foreground',
    muted: 'text-sm text-muted-foreground',
  },
  
  /**
   * Layout variants
   */
  layout: {
    section: 'py-12',
    grid: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
    flexBetween: 'flex justify-between items-center',
    flexCenter: 'flex justify-center items-center',
  },
}

/**
 * Usage example:
 * 
 * import { cn, twStyles } from '@/utils/tailwind-utils'
 * 
 * function Button({ className, variant = 'primary', size = 'md', ...props }) {
 *   return (
 *     <button 
 *       className={cn(
 *         twStyles.button.base,
 *         twStyles.button[variant],
 *         twStyles.button[size],
 *         className
 *       )}
 *       {...props}
 *     />
 *   )
 * }
 */
